import React, { useState, useEffect, useCallback } from "react";
import { Container, Navbar, Nav } from "react-bootstrap";
import { Routes, Route, NavLink, useLocation } from "react-router-dom";
import TimelineCalculator from "./components/TimelineCalculator";
import HydrationCalculator from "./components/HydrationCalculator";
import StarterCalculator from "./components/StarterCalculator";
import BakersPercentageCalculator from "./components/BakersPercentageCalculator";
import Products from "./components/Products";
import LandingPage from "./components/LandingPage";
import ThemeToggle from "./components/ThemeToggle";
import Footer from "./components/Footer";
import TemperatureGuide from "./components/TemperatureGuide";
import TroubleshootingGuide from "./components/TroubleshootingGuide";
import Glossary from "./components/Glossary";
import Recipes from "./components/Recipes";
import { calculateTimeline } from "./utils/timeline";
import { initGA, trackPageView } from "./utils/analytics";
import { decodeScheduleParams } from "./utils/urlParams";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";

// Initialize GA with your measurement ID
const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
console.log("GA Measurement ID:", measurementId); // Debug log
if (!measurementId) {
  console.warn("Google Analytics Measurement ID is not set");
} else {
  initGA(measurementId);
}

function App() {
  const location = useLocation();
  const [timeline, setTimeline] = useState(null);
  const [temperature, setTemperature] = useState(75); // Default to 75°F (24°C)
  const [isDark, setIsDark] = useState(() => {
    // Check if user has a saved preference
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      return savedTheme === "dark";
    }
    // If no saved preference, use system preference
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const handleTimeSubmit = useCallback(
    (timeInput, customDurations = {}) => {
      const { time, direction } = timeInput;
      const calculatedTimeline = calculateTimeline(
        time,
        temperature,
        customDurations,
        direction
      );
      setTimeline(calculatedTimeline);
    },
    [temperature]
  );

  // Handle URL parameters on initial load and URL changes
  useEffect(() => {
    if (location.pathname === "/timeline") {
      const params = decodeScheduleParams(location.search);
      if (params) {
        setTemperature(params.temperature);
        handleTimeSubmit(params.timeInput, params.customDurations);
      }
    }
  }, [location.pathname, location.search, handleTimeSubmit]);

  useEffect(() => {
    // Update theme when it changes
    document.body.setAttribute("data-bs-theme", isDark ? "dark" : "light");
    localStorage.setItem("theme", isDark ? "dark" : "light");
  }, [isDark]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      if (!localStorage.getItem("theme")) {
        setIsDark(e.matches);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Track page views when location changes
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);

  const handleTemperatureChange = (newTemp) => {
    setTemperature(newTemp);
    if (timeline) {
      const recalculatedTimeline = calculateTimeline(
        timeline[0].startTime,
        newTemp,
        timeline[0].customDurations || {},
        timeline[0].startTime > timeline[timeline.length - 1].startTime
          ? "reverse"
          : "forward"
      );
      setTimeline(recalculatedTimeline);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar
        bg={isDark ? "dark" : "light"}
        variant={isDark ? "dark" : "light"}
        expand="lg"
        className="mb-4"
      >
        <Container>
          <Navbar.Brand as={NavLink} to="/" className="text-decoration-none">
            🍞 SourdoughMath
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
              <Nav.Link
                as={NavLink}
                to="/timeline"
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                Timeline
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/recipes"
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                Recipes
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/hydration"
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                Hydration
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/starter"
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                Starter Ratio
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/bakers-percentage"
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                Baker's %
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/temperature-guide"
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                Temperature Guide
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/troubleshooting"
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                Troubleshooting
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/glossary"
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                Glossary
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/products"
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                Shop
              </Nav.Link>
            </Nav>
            <ThemeToggle isDark={isDark} onToggle={() => setIsDark(!isDark)} />
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container className="py-4 flex-grow-1">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/timeline"
            element={
              <TimelineCalculator
                temperature={temperature}
                onTemperatureChange={handleTemperatureChange}
                onTimeSubmit={handleTimeSubmit}
                timeline={timeline}
              />
            }
          />
          <Route path="/recipes" element={<Recipes />} />
          <Route path="/hydration" element={<HydrationCalculator />} />
          <Route path="/starter" element={<StarterCalculator />} />
          <Route
            path="/bakers-percentage"
            element={<BakersPercentageCalculator />}
          />
          <Route path="/temperature-guide" element={<TemperatureGuide />} />
          <Route path="/troubleshooting" element={<TroubleshootingGuide />} />
          <Route path="/glossary" element={<Glossary />} />
          <Route path="/products" element={<Products />} />
        </Routes>
      </Container>

      <Footer />
    </div>
  );
}

export default App;
